import { FC, KeyboardEvent, memo } from 'react';
import {
  useIsSearchActive,
  useListSearchActions,
  useSearchQuery,
} from '@components/MainStage/listSearch.store';
import { Box, IconButton, InputBase, Stack, Typography } from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { CloseCircleIcon, SearchIcon } from '@utils/iconsDefs';

type Props = {
  count: number;
};

export const ListMenuHeader: FC<Props> = memo(({ count }) => {
  const isActive = useIsSearchActive();
  const query = useSearchQuery();
  const { activate, deactivate, setQuery } = useListSearchActions();

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      deactivate();
    }
  };

  if (!isActive) {
    return (
      <Box
        p={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <Typography variant="h3" sx={{ whiteSpace: 'nowrap' }}>
          Entities list ({count})
        </Typography>

        <IconButton
          size="small"
          variant="simple"
          sx={{
            borderRadius: '4px',
            p: '3px',
            flexShrink: 0,
            border: 'none',
            outline: 'none',
          }}
          onClick={activate}
          aria-label="Search entities"
        >
          <SearchIcon width={20} height={20} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{
          height: 44,
          px: 3,
          borderRadius: '12px',
          border: `1px solid ${themePalette.grey[700]}`,

          '&:focus-within': {
            border: `2px solid ${themePalette.primary.main}`,
          },
        }}
      >
        <SearchIcon width={20} height={20} style={{ flexShrink: 0 }} />

        <InputBase
          autoFocus
          fullWidth
          value={query}
          placeholder=" Search by name…"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            fontSize: 14,
            color: themePalette.grey[1000],
          }}
        />

        <IconButton size="small" onClick={deactivate} aria-label="Exit search">
          <CloseCircleIcon width={20} height={20} style={{ flexShrink: 0 }} />
        </IconButton>
      </Stack>
    </Box>
  );
});
